import { FC } from 'react'
import { Box, Button, Typography, CircularProgress } from '@mui/material'
import { useParams, Link } from 'react-router-dom'
import { CONTRACT_DOCUMENT_TYPE } from 'services/contracts/contracts.consts'
import AdditionalServicesOnlinePaymentButton from 'components/AdditionalServices/AdditionalServicesOnlinePaymentButton'
import OperationsFrozenMessage from 'components/OperationsFrozenMessage'
import { useDebitNote } from 'services/pay/pay.hooks'
import { useLeaseContractsList } from 'services/contracts/hooks/useLeaseContracts'
import { useIntl } from 'react-intl'

const DebitNotePayPage: FC = () => {
  const { documentId, id } = useParams()
  const { formatMessage } = useIntl()
  const { getActiveContract } = useLeaseContractsList()
  const isOperationsFrozen = getActiveContract(Number(id))?.isOperationsFrozen

  const {
    debitNoteDetails,
    isLoading: invoiceIsLoading,
    error
  } = useDebitNote({
    id: Number(documentId),
    leaseContractId: Number(id)
  })

  if (invoiceIsLoading && !!invoiceIsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box>
        <Typography variant='h5' sx={{ textAlign: 'center' }}>
          {formatMessage({
            id: 'paymentScheduleDocumnet.missingDocument',
            defaultMessage: 'Brak dokumentu'
          })}
        </Typography>
        <Box display='flex' justifyContent='space-between' mt={4}>
          <Button component={Link} to='/'>
            Idź do panelu
          </Button>
        </Box>
      </Box>
    )
  }

  return (
    <Box p={4}>
      <Box>
        <Box>
          <Typography variant='h4'>
            {formatMessage({
              id: 'paymentScheduleDocumnetTitle.debitNoteDetails',
              defaultMessage: 'Nota obciążeniowa'
            })}
          </Typography>
          <Typography variant='h5'>
            {debitNoteDetails?.number ||
              formatMessage({
                id: 'paymentScheduleDocumnet.awaiting',
                defaultMessage: 'Oczekuje na wystawienie'
              })}
          </Typography>
        </Box>
        <Box mt={4} mb={4}>
          {debitNoteDetails?.positions.map((item) => (
            <Box
              key={`${debitNoteDetails.number}_${item.id}`}
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography>{item.title}</Typography>
              <Typography fontWeight={600} textAlign='right'>
                {item.amount} {debitNoteDetails?.currency}
              </Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant='h5'>
            {formatMessage({
              id: 'paymentScheduleDocumnet.receivable',
              defaultMessage: 'Należność'
            })}
            :
          </Typography>
          <Typography fontWeight={600} mb={2}>
            {debitNoteDetails?.amount} {debitNoteDetails?.currency}
          </Typography>
          <Typography variant='h6'>
            {formatMessage({
              id: 'paymentScheduleDocumnet.paid',
              defaultMessage: 'Opłacono'
            })}
            :
          </Typography>
          <Typography fontWeight={600}>
            {debitNoteDetails?.paymentAmount} {debitNoteDetails?.currency}
          </Typography>
        </Box>
      </Box>
      {isOperationsFrozen && <OperationsFrozenMessage />}
      <Box display='flex' justifyContent='space-between' mt={4}>
        <Button component={Link} to='/'>
          Idź do panelu
        </Button>
        {!isOperationsFrozen &&
          !debitNoteDetails?.isPaid &&
          ['PLN'].includes(debitNoteDetails?.currency ?? '') && (
            <AdditionalServicesOnlinePaymentButton
              documentType={CONTRACT_DOCUMENT_TYPE.debitNote}
              documentId={Number(id)}
            />
          )}
      </Box>
    </Box>
  )
}

export default DebitNotePayPage
